import { LoaderDataTable } from '@/components/Menus/LoaderDataTable/LoaderDataTable';
import { NoResultsDataTable } from '@/components/Menus/NoResultsDataTable/NoResultsDataTable';
import { CustomDomainModal } from '@/components/Modals/CustomDomainModal/CustomDomainModal';
import { CustomDomainService } from '@/services/CustomDomainService';
import { useCustomDomainsStore } from '@/stores/useCustomDomainsStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { IDomain } from '@/types/domain';
import { getRelativeTime } from '@/utils/DateUtility';
import {
  faCheckCircle,
  faExclamation,
  faExclamationCircle,
  faGlobe,
  faPencil,
  faPlug,
  faTrash,
  faWarning,
  faCircleInfo,
  faSync,
  faCrown,
  faCalendarPlus,
  faBadgeCheck,
  faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Grid,
  Modal,
  Space,
  Switch,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { modals } from '@mantine/modals';
import { DNSRecordsModal } from '@/components/Modals/DNSRecordsModal/DNSRecordsModal';
import { useCampaignsStore } from '@/stores/useCampaignsStore';
import { usePermissionsStore } from '@/stores/usePermissionsStore';
import { checkPermission, isTrialAccount, isUserCollaborator } from '@/utils/CommonUtility';
import { useUserStore } from '@/stores/useUserStore';
import { UpgradePlanMessage } from '@/components/Billing/UpgradePlanMessage';
import { usePlanStore } from '@/stores/usePlanStore';
import { APP_ENVIRONMENT, SHORTENER_CUSTOM_DOMAIN } from '@/constants/Constants';
import { DomainPurchaseModal } from '@/components/Modals/DomainPurchaseModal/DomainPurchaseModal';
// import ReplugLogo from '@/assets/images/logos/replug-logo.svg';
import ReplugLogo from '@/assets/images/logos/replug-favicon.svg';
import { useNavigate } from 'react-router-dom';

const DELETE_WORKSPACE_OPTIONS = {
  ONLY_THIS_WORKSPACE: 'workspace',
  ALL_WORKSPACES: 'all_workspaces',
};

/**
 * Check if a domain is within one month of expiry
 * @param expiryDate The expiry date of the domain
 * @returns boolean True if the domain is within one month of expiry
 */
const isWithinOneMonthOfExpiry = (expiryDate: string | Date): boolean => {
  if (!expiryDate) return false;

  const expiry = new Date(expiryDate);
  const today = new Date();

  // Calculate one month from today
  const oneMonthFromNow = new Date();
  oneMonthFromNow.setMonth(today.getMonth() + 1);

  // Domain is within one month of expiry if expiry date is before one month from now
  // but after or equal to today (not expired yet)
  return expiry <= oneMonthFromNow && expiry >= today;
};

export function CustomDomainsPage() {
  const [defaultWorkspace] = useWorkspaceStore((state) => [state.defaultWorkspace]);
  useDocumentTitle('Custom Domains | ' + defaultWorkspace?.agency?.name);

  const [plan] = usePlanStore((state) => [state.plan]);
  const [fetchCampaigns] = useCampaignsStore((state) => [state.fetchCampaigns]);
  const [opened, setOpened] = useState(false);
  const [checkConnectivityFlag, setCheckConnectivityFlag] = useState(false);
  const [dnsModalOpened, setDnsModalOpened] = useState(false);
  const [records, setRecords] = useState<any>([]);
  const [list, setList, loading, fetchCustomDomains] = useCustomDomainsStore((state) => [
    state.customDomains,
    state.setCustomDomains,
    state.loading,
    state.fetchCustomDomains,
  ]);
  const [editDomain, setEditDomain] = useState<IDomain | null>(null);
  const [defaultPermissions] = usePermissionsStore((state) => [state.defaultPermissions]);
  const [user] = useUserStore((state) => [state.user]);
  const [purchaseModalOpened, setPurchaseModalOpened] = useState(false);
  const [isRenewing, setIsRenewing] = useState(false);
  const navigate = useNavigate();
  const [setUpgradeModalState] = usePlanStore((state) => [state.setUpgradeModalState]);

  const handleCheckConnectivity = async (domain: IDomain) => {
    setCheckConnectivityFlag(true)
    await new CustomDomainService()
      .checkConnectivity(defaultWorkspace?._id || '', domain.url, domain?._id || '')
      .then((res) => {
        if (res.data.status) {
          setCheckConnectivityFlag(false)
          const newList = list.map((item: IDomain) => {
            if (item?._id === domain?._id) {
              item.is_connected = res.data.data;
            }
            return item;
          });

          setList(newList);

          notifications.show({
            color: res.data.data ? 'green' : 'red',
            message: res.data.message,
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  // Delete custom domain

  const handleDeleteCustomDomain = async (
    domain: IDomain,
    type: 'workspace' | 'all_workspaces' | string = 'workspace'
  ) => {
    await new CustomDomainService()
      .delete(defaultWorkspace?._id || '', domain._id, type)
      .then((res) => {
        if (res.data.status) {
          const newList = list.filter((item: IDomain) => item._id !== domain._id);
          setList(newList);
          fetchCampaigns(defaultWorkspace?._id || '');
          modals.closeAll();
          notifications.show({
            color: 'green',
            message: res.data.message,
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleDNSRecords = async (domain: IDomain) => {
    setDnsModalOpened(true);
    await new CustomDomainService()
      .dnsRecords(defaultWorkspace?._id || '', domain._id)
      .then((res) => {
        if (res.data.status) {
          setRecords(res.data.data);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleRenewDomain = async (domain: IDomain) => {
    if (!domain) return;

    setIsRenewing(true);
    try {
      // Always renew for 1 year
      const renewYears = 1;

      const response = await new CustomDomainService().renewDomain(
        defaultWorkspace?._id || '',
        domain._id,
        renewYears
      );

      if (response.data.status) {
        // Update the domain in the list with the new expiry date
        const updatedList = list.map((item: IDomain) => {
          if (item._id === domain._id) {
            return {
              ...item,
              expiry_date: response.data.data.expiry_date
            };
          }
          return item;
        });
        setList(updatedList);
        modals.closeAll();

        notifications.show({
          color: 'green',
          message: `Domain renewed successfully! New expiry date: ${new Date(response.data.data.expiry_date).toLocaleDateString()}`
        });

      } else {
        notifications.show({
          color: 'red',
          message: 'Error renewing domain: ' + (response.data.message || 'Unknown error')
        });
      }
    } catch (error: any) {
      console.error('Error renewing domain:', error);
      notifications.show({
        color: 'red',
        message: error?.response?.data?.message || 'Please try again.'
      });
    } finally {
      setIsRenewing(false);
    }
  };

  const handleStatueChange = async (domain: IDomain, status: any) => {
    await new CustomDomainService()
      .updateStatus(defaultWorkspace?._id || '', domain?._id, status)
      .then((res) => {
        if (res.data.status) {
          notifications.show({
            color: 'green',
            message: res.data.message,
          });
          fetchCustomDomains(defaultWorkspace?._id || '');
        }
      })
      .catch((err) => {
        console.error(err);
        if (err.response.status === 422 || err.response.status === 400 || err.response.status === 404) {
          notifications.show({
            color: 'red',
            message: err.response.data.message,
          });
        }
      });
  };

  const handleToggleAutoRenewal = async (domain: IDomain) => {
    // For trial users, redirect to billing page
    if (isTrialAccount(plan)) {
      navigate('/account/billing');
      return;
    }

    // Create a temporary updated domain for immediate UI feedback
    const newAutoRenewalValue = !domain.auto_renewal_enabled;
    console.log('Current auto_renewal_enabled value:', domain.auto_renewal_enabled);
    console.log('New auto_renewal_enabled value:', newAutoRenewalValue);

    try {
      // Make the API call first before updating the UI
      console.log('Making API call to toggle auto-renewal...');
      const response = await new CustomDomainService().toggleAutoRenewal(
        defaultWorkspace?._id || '',
        domain._id
      );

      console.log('API response:', response.data);

      if (response.data.status) {
        // Only update the UI after successful API call
        const updatedDomains = list.map((item: IDomain) => {
          if (item._id === domain._id) {
            const updatedItem = {
              ...item,
              auto_renewal_enabled: response.data.data.auto_renewal_enabled
            };
            console.log('Updated domain item:', updatedItem);
            return updatedItem;
          }
          return item;
        });

        // Update the state with the server response
        setList([...updatedDomains]);

        notifications.show({
          color: 'green',
          message: response.data.message || `Auto-renewal ${response.data.data.auto_renewal_enabled ? 'enabled' : 'disabled'} successfully.`,
        });
      } else {
        notifications.show({
          color: 'red',
          message: response.data.message || 'Failed to toggle auto-renewal. Please try again.',
        });
      }
    } catch (error: any) {
      console.error('Error toggling auto-renewal:', error);

      notifications.show({
        color: 'red',
        message: error?.response?.data?.message || 'Failed to toggle auto-renewal. Please try again.',
      });
    }
  };

  useEffect(() => {
    fetchCustomDomains(defaultWorkspace?._id || '');
  }, []);

  return (
    <>
      <DNSRecordsModal opened={dnsModalOpened} setOpened={setDnsModalOpened} records={records} />
      <CustomDomainModal opened={opened} setOpened={setOpened} domain={editDomain} />
      <DomainPurchaseModal opened={purchaseModalOpened} setOpened={setPurchaseModalOpened} plan={plan} />
      {!plan?.limits?.custom_domains ? (
        <UpgradePlanMessage />
      ) : (
        <>
          {/* Main Header with Buttons */}
          <Box
            style={{
              backgroundColor: 'var(--mantine-color-white)',
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)',
              marginBottom: '16px',
              padding: '16px',
            }}
            ml={16}
            mr={16}
          >
            <Grid>
              <Grid.Col span={5}>
                {(defaultPermissions && checkPermission(defaultPermissions, ['add_cDomains'])) && <>
                  <Button
                    size="xs"
                    onClick={() => {
                      setEditDomain(null);
                      setOpened(true);
                    }}
                  >
                    Add Domain
                  </Button>
                  <Tooltip
                    label={
                      list.some(domain => domain.purchased_through_platform)
                        ? "You can claim only one free domain per workspace"
                        : isTrialAccount(plan)
                          ? "Upgrade to a paid plan to claim a free domain."
                          : ""
                    }
                    disabled={!list.some(domain => domain.purchased_through_platform) && !isTrialAccount(plan)}
                  >
                    <span>
                      <Button
                        variant="outline"
                        mr={8}
                        ml={8}
                        size='xs'
                        leftSection={<FontAwesomeIcon icon={faCrown} />}
                        onClick={() => isTrialAccount(plan) ? navigate('/account/billing') : setPurchaseModalOpened(true)}
                        disabled={list.some(domain => domain.purchased_through_platform) || isTrialAccount(plan)}
                      >
                        {"Claim Free .link Domain"}
                      </Button>
                    </span>
                  </Tooltip></>}
              </Grid.Col>
              <Grid.Col span={2}>
                <Flex align={'center'} pl={'lg'}>
                  <Text size="md" fw="bold" mr="sm">
                    Domains
                  </Text>
                  <Tooltip
                    multiline
                    w={450}
                    fz="xs"
                    label="Custom Domains enable you to use your own branded domains for short links, enhancing brand visibility and trust."
                  >
                    <FontAwesomeIcon
                      style={{
                        color: 'var(--mantine-color-gray-6)',
                      }}
                      icon={faCircleInfo}
                    />
                  </Tooltip>
                </Flex>
              </Grid.Col>
            </Grid>
          </Box>

          {/* Claimed Domains Section */}
          <Box
            style={{
              backgroundColor: 'var(--mantine-color-white)',
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)',
              marginBottom: '16px',
            }}
            ml={16}
            mr={16}
          >
            <Box p={16} pb={8}>
              <Flex align="center" mb={8}>
                <img src={ReplugLogo} alt="Replug Logo" width={25} height={25} />
                <Text fw={600} ml={8} size="sm">Provisioned by Replug</Text>
              </Flex>
            </Box>

            {list.some(domain => domain.purchased_through_platform) ? (
              <Table stickyHeader verticalSpacing={'md'}>
                <Table.Thead h={20} bg={'gray.1'} c="gray.7" fz="xs">
                  <Table.Tr>
                    <Table.Th ta="center">Connectivity</Table.Th>
                    {(defaultPermissions && checkPermission(defaultPermissions, ['edit_cDomains'])) && <Table.Th ta="center">Set as Default</Table.Th>}
                    <Table.Th w={'20%'}>URL</Table.Th>
                    <Table.Th>404 Redirect URL</Table.Th>
                    <Table.Th>Branded Domain URL</Table.Th>
                    <Table.Th ta="center">Created at</Table.Th>
                    <Table.Th ta="center">Expiry Date</Table.Th>
                    <Table.Th ta="center">Auto Renewal</Table.Th>
                    {(APP_ENVIRONMENT !== 'production' || list.some(domain => isWithinOneMonthOfExpiry(domain.expiry_date || ''))) && (
                      <Table.Th ta="center">Renewal Status</Table.Th>
                    )}
                    <Table.Th ta={'center'}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {loading ? (
                    <LoaderDataTable colSpan={(APP_ENVIRONMENT !== 'production' || list.some(domain => isWithinOneMonthOfExpiry(domain.expiry_date || ''))) ? 10 : 9} />
                  ) : (
                    list.filter(domain => domain.purchased_through_platform).map((item, index) => (
                      <Table.Tr key={item._id}>
                        <Table.Td ta="center">
                          {item.is_connected ? (
                            <Tooltip label="Domain is successfully connected and active">
                              <Flex align="center" justify="center">
                                <Box
                                  py={5}
                                  px={12}
                                  style={{
                                    backgroundColor: 'var(--mantine-color-green-1)',
                                    borderRadius: '20px',
                                    border: '1px solid var(--mantine-color-green-3)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faBadgeCheck}
                                    style={{
                                      color: 'var(--mantine-color-green-6)',
                                      marginRight: '6px',
                                      fontSize: '13px'
                                    }}
                                  />
                                  <Text fz={12} fw={600} c="green.7">Connected</Text>
                                </Box>
                              </Flex>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              multiline
                              w={250}
                              label="Domain provisioning in progress. Click 'Check Connectivity' to verify your DNS configuration and finish setup."
                              //@ts-ignore
                              align="center"
                            >
                              <Flex align="center" justify="center">
                                <Box
                                  py={5}
                                  px={12}
                                  style={{
                                    backgroundColor: 'var(--mantine-color-yellow-1)',
                                    borderRadius: '20px',
                                    border: '1px solid var(--mantine-color-yellow-3)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faSpinner}
                                    style={{
                                      color: 'var(--mantine-color-yellow-6)',
                                      marginRight: '6px',
                                      fontSize: '13px'
                                    }}
                                    spin
                                  />
                                  <Text fz={12} fw={600} c="yellow.7">Provisioning</Text>
                                </Box>
                              </Flex>
                            </Tooltip>
                          )}
                        </Table.Td>
                        {(defaultPermissions && checkPermission(defaultPermissions, ['edit_cDomains'])) && <Table.Td ta="center">
                          <Center>
                            <Switch
                              size="xs"
                              checked={item.is_default}
                              onChange={(event) => handleStatueChange(item, event.currentTarget.checked)}
                              styles={{
                                track: {
                                  cursor: 'pointer',
                                },
                              }}
                            />
                          </Center>
                        </Table.Td>}
                        <Table.Td>
                          <Flex align={'center'}>
                            <FontAwesomeIcon icon={faGlobe} />
                            <Text ml={8} fz={13}>{item.url}</Text>
                            <Tooltip label="Claimed Through Replug">
                              <Box ml={8}>
                                <FontAwesomeIcon icon={faCrown} style={{ color: 'var(--mantine-color-yellow-5)' }} />
                              </Box>
                            </Tooltip>
                          </Flex>
                        </Table.Td>
                        <Table.Td>
                          <Text fz={13}>{item['404_redirect_url'] || 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text fz={13}>{item['branded_domain_url'] || 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td ta="center">
                          <Text fz={13}>{getRelativeTime(item.created_at)}</Text>
                        </Table.Td>
                        <Table.Td ta="center">
                          <Text fz={13}>{new Date(item.expiry_date || '').toLocaleDateString()}</Text>
                        </Table.Td>
                        <Table.Td ta="center">
                          {item.purchased_through_platform && item.expiry_date && (
                            <Tooltip
                              label={isTrialAccount(plan)
                                ? "Upgrade to a paid plan to enable auto-renewal"
                                : (item.auto_renewal_enabled ? "Disable auto-renewal" : "Enable auto-renewal")}
                            >
                              <Box>
                                <Center>
                                  <Switch
                                    key={`switch-${item._id}`}
                                    size="xs"
                                    checked={!!item.auto_renewal_enabled}
                                    onChange={() => handleToggleAutoRenewal(item)}
                                    disabled={isTrialAccount(plan)}
                                    styles={{
                                      track: {
                                        cursor: isTrialAccount(plan) ? 'not-allowed' : 'pointer',
                                      },
                                    }}
                                  />
                                </Center>
                              </Box>

                            </Tooltip>
                          )}
                          {(!item.purchased_through_platform || !item.expiry_date) && (
                            <Text fz={13}>N/A</Text>
                          )}
                        </Table.Td>
                        {(APP_ENVIRONMENT !== 'production' || isWithinOneMonthOfExpiry(item.expiry_date || '')) && (
                          <Table.Td ta="center">
                            {item.purchased_through_platform && item.expiry_date && (
                              <>
                                <Flex align="center" justify="center">
                                  <Tooltip label={isWithinOneMonthOfExpiry(item.expiry_date || '') ? "Domain expiry approaching" : "Renew domain"}>
                                    <Box
                                      py={5}
                                      px={12}
                                      style={{
                                        backgroundColor: isWithinOneMonthOfExpiry(item.expiry_date || '') ? 'var(--mantine-color-orange-1)' : 'var(--mantine-color-blue-1)',
                                        borderRadius: '20px',
                                        border: `1px solid ${isWithinOneMonthOfExpiry(item.expiry_date || '') ? 'var(--mantine-color-orange-3)' : 'var(--mantine-color-blue-3)'}`,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => {
                                        modals.open({
                                          size: 'lg',
                                          title: <Title order={5}>Renew Domain</Title>,
                                          children: (
                                            <>
                                              <Text mb={16} fz={14}>
                                                Are you sure you want to renew <strong>{item.url}</strong> for 1 year?
                                              </Text>
                                              <Alert
                                                mb={16}
                                                title="Domain Renewal"
                                                icon={<FontAwesomeIcon icon={faCalendarPlus} />}
                                              >
                                                <Text fz={14} fw={500}>
                                                  Current expiry date: <strong>{new Date(item.expiry_date || '').toLocaleDateString()}</strong>
                                                  <br />
                                                  Renewal price: <strong>$12.00</strong> for next year
                                                </Text>
                                              </Alert>
                                              <Flex justify={'center'}>
                                                <Button
                                                  variant="outline"
                                                  mr={12}
                                                  onClick={() => modals.closeAll()}
                                                >
                                                  Cancel
                                                </Button>
                                                <Button
                                                  loading={isRenewing}
                                                  onClick={() => handleRenewDomain(item)}
                                                >
                                                  Renew Domain
                                                </Button>
                                              </Flex>
                                            </>
                                          ),
                                        });
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faCalendarPlus}
                                        style={{
                                          color: isWithinOneMonthOfExpiry(item.expiry_date || '') ? 'var(--mantine-color-yellow-6)' : 'var(--mantine-color-blue-6)',
                                          marginRight: '6px',
                                          fontSize: '13px'
                                        }}
                                      />
                                      <Text fz={12} fw={600} c={isWithinOneMonthOfExpiry(item.expiry_date || '') ? "yellow.7" : "blue.7"}>
                                        {APP_ENVIRONMENT !== 'production' ? "Renew (Dev)" : (isWithinOneMonthOfExpiry(item.expiry_date || '') ? "Expires Soon" : "Renew Now")}
                                      </Text>
                                    </Box>
                                  </Tooltip>
                                </Flex>
                              </>
                            )}
                            {(!item.purchased_through_platform || !item.expiry_date) && (
                              <Text fz={13}>N/A</Text>
                            )}
                          </Table.Td>
                        )}
                        <Table.Td ta="center">
                          <Flex align={'center'} justify={'center'}>
                            <Tooltip label="Check connectivity">
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                onClick={() => handleCheckConnectivity(item)}
                                disabled={checkConnectivityFlag}
                              >
                                <FontAwesomeIcon icon={faPlug} />
                              </ActionIcon>
                            </Tooltip>
                            <Box ml={8} />
                            <Tooltip label="DNS records">
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                onClick={() => handleDNSRecords(item)}
                              >
                                <FontAwesomeIcon icon={faGlobe} />
                              </ActionIcon>
                            </Tooltip>
                            <Box ml={8} />
                            {(defaultPermissions && checkPermission(defaultPermissions, ['edit_cDomains'])) && <Tooltip
                              label="Edit"
                              onClick={() => {
                                setEditDomain(item);
                                setOpened(true);
                              }}
                            >
                              <ActionIcon variant="subtle" color="gray">
                                <FontAwesomeIcon icon={faPencil} />
                              </ActionIcon>
                            </Tooltip>}
                            <Box ml={8} />
                            {/* {(defaultPermissions && checkPermission(defaultPermissions, ['delete_cDomains'])) && <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => {
                                  modals.open({
                                    size: 'lg',
                                    title: <Title order={5}>Delete Custom Domain</Title>,
                                    children: (
                                      <>
                                        <Text mb={16} fz={14}>
                                          Are you sure you want to delete this custom domain? This
                                          action cannot be undone.
                                        </Text>
                                        <Alert
                                          mb={16}
                                          color="red"
                                          title="Warning"
                                          style={{
                                            color: 'var(--mantine-color-red-6)',
                                          }}
                                          icon={<FontAwesomeIcon icon={faExclamationCircle} />}
                                        >
                                          <Text fz={14} fw={500}>
                                            Your campaigns using this domain will be affected. Your links will be reverted to the default short domain and some of them may not work properly if the same slug (unique identifier) is already used by other users.
                                          </Text>
                                        </Alert>
                                        <Flex justify={'center'}>
                                          <Button
                                            variant="outline"
                                            color="red"
                                            mr={12}
                                            onClick={() =>
                                              handleDeleteCustomDomain(
                                                item,
                                                DELETE_WORKSPACE_OPTIONS.ONLY_THIS_WORKSPACE
                                              )
                                            }
                                          >
                                            Delete from this workspace
                                          </Button>
                                          {user?.migrated_to_v2 && <Button
                                            color="red"
                                            onClick={() =>
                                              handleDeleteCustomDomain(
                                                item,
                                                DELETE_WORKSPACE_OPTIONS.ALL_WORKSPACES
                                              )
                                            }
                                          >
                                            Delete from all workspaces
                                          </Button>}
                                        </Flex>
                                      </>
                                    ),
                                  });
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </ActionIcon> */}
                            {/* </Tooltip>} */}
                          </Flex>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            ) : (
              <Box
                p={12}
                style={{
                  background: 'linear-gradient(to right, #f8f9fa, #f0f4f8)',
                  borderRadius: '8px',
                  border: '1px dashed #dee2e6',
                  margin: '0'
                }}
              >
                <Flex direction="row" align="center" justify="space-between">
                  <Flex align="center">
                    <Box
                      mr={12}
                      style={{
                        backgroundColor: 'rgba(255, 217, 102, 0.2)',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCrown}
                        style={{
                          color: 'var(--mantine-color-yellow-5)',
                          fontSize: '18px'
                        }}
                      />
                    </Box>
                    <Box p={5}>
                      <Text fw={600} size="sm" mb={4} c="gray.8">Get your professional .link domain</Text>
                      <Text size="xs" c="dimmed">
                        {isTrialAccount(plan)
                          ? "Upgrade to a paid plan to claim a free domain for 1 year."
                          : "Claim a free domain for 1 year to enhance your brand visibility."}
                      </Text>
                    </Box>
                  </Flex>
                  <Button
                    variant="filled"
                    size="xs"
                    leftSection={<FontAwesomeIcon icon={faCrown} />}
                    onClick={() => {
                      if (isTrialAccount(plan)) {
                        setUpgradeModalState(true);
                        navigate('/account/billing');
                      } else {
                        setPurchaseModalOpened(true);
                      }
                    }}
                    disabled={list.some(domain => domain.purchased_through_platform)}
                    mr={8}
                  >
                    {isTrialAccount(plan) ? "Upgrade Plan" : "Claim Domain"}
                  </Button>
                </Flex>
              </Box>
            )}
          </Box>

          {/* Other Custom Domains Section */}
          <Box
            style={{
              backgroundColor: 'var(--mantine-color-white)',
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)',
              height: '100%',
              flexGrow: 1,
            }}
            ml={16}
            mr={16}
          >
            <Box p={16} pb={0}>
              <Flex align="center" mb={8}>
                <FontAwesomeIcon icon={faGlobe} style={{ color: 'var(--mantine-color-blue-5)' }} />
                <Text fw={600} ml={8} mb={2} size="sm">Custom Domains</Text>
              </Flex>
            </Box>
            <Table stickyHeader verticalSpacing={'md'}>
              <Table.Thead h={20} bg={'gray.1'} c="gray.7" fz="xs">
                <Table.Tr>
                  <Table.Th ta="center">Connectivity</Table.Th>
                  {(defaultPermissions && checkPermission(defaultPermissions, ['edit_cDomains'])) && <Table.Th ta="center">Set as Default</Table.Th>}
                  <Table.Th w={'20%'}>URL</Table.Th>
                  <Table.Th>404 Redirect URL</Table.Th>
                  <Table.Th>Branded Domain URL</Table.Th>
                  <Table.Th ta="center">Created at</Table.Th>
                  <Table.Th ta={'center'}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading ? (
                  <LoaderDataTable colSpan={(APP_ENVIRONMENT !== 'production' || list.some(domain => isWithinOneMonthOfExpiry(domain.expiry_date || ''))) ? 10 : 9} />
                ) : (
                  <>
                    {list.filter(domain => !domain.purchased_through_platform).length === 0 ? (
                      <NoResultsDataTable
                        colSpan={(APP_ENVIRONMENT !== 'production' || list.some(domain => isWithinOneMonthOfExpiry(domain.expiry_date || ''))) ? 10 : 9}
                        data={[]}
                        noResultsMessage="You do not have any custom domains created."
                      />
                    ) : (
                      list.filter(domain => !domain.purchased_through_platform).map((item, index) => (
                        <Table.Tr key={item._id}>
                          <Table.Td ta="center">
                            {item.is_connected ? (
                              <Tooltip label="Domain is connected">
                                <FontAwesomeIcon
                                  style={{
                                    color: 'var(--mantine-color-green-6)',
                                  }}
                                  icon={faCheckCircle}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip
                                multiline
                                w={250}
                                label="Domain is not connected, please follow the instructions to point custom domain."
                              >
                                <FontAwesomeIcon
                                  style={{
                                    color: 'var(--mantine-color-red-6)',
                                  }}
                                  icon={faWarning}
                                />
                              </Tooltip>
                            )}
                          </Table.Td>
                          {(defaultPermissions && checkPermission(defaultPermissions, ['edit_cDomains'])) && <Table.Td ta="center">
                            <Center>
                              <Switch
                                size="xs"
                                checked={item.is_default}
                                onChange={(event) => handleStatueChange(item, event.currentTarget.checked)}
                                styles={{
                                  track: {
                                    cursor: 'pointer',
                                  },
                                }}
                              />
                            </Center>
                          </Table.Td>}
                          <Table.Td>
                            <Flex align={'center'}>
                              <FontAwesomeIcon icon={faGlobe} />
                              <Text ml={8} fz={13}>{item.url}</Text>
                            </Flex>
                          </Table.Td>
                          <Table.Td>
                            <Text fz={13}>{item['404_redirect_url'] || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text fz={13}>{item['branded_domain_url'] || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td ta="center">
                            <Text fz={13}>{getRelativeTime(item.created_at)}</Text>
                          </Table.Td>
                          <Table.Td ta="center">
                            <Flex align={'center'} justify={'center'}>
                              <Tooltip label="Check connectivity">
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  onClick={() => handleCheckConnectivity(item)}
                                  disabled={checkConnectivityFlag}
                                >
                                  <FontAwesomeIcon icon={faPlug} />
                                </ActionIcon>
                              </Tooltip>
                              <Box ml={8} />
                              <Tooltip label="DNS records">
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  onClick={() => handleDNSRecords(item)}
                                >
                                  <FontAwesomeIcon icon={faGlobe} />
                                </ActionIcon>
                              </Tooltip>
                              <Box ml={8} />
                              {(defaultPermissions && checkPermission(defaultPermissions, ['edit_cDomains'])) && <Tooltip
                                label="Edit"
                                onClick={() => {
                                  setEditDomain(item);
                                  setOpened(true);
                                }}
                              >
                                <ActionIcon variant="subtle" color="gray">
                                  <FontAwesomeIcon icon={faPencil} />
                                </ActionIcon>
                              </Tooltip>}
                              <Box ml={8} />
                              {(defaultPermissions && checkPermission(defaultPermissions, ['delete_cDomains'])) && <Tooltip label="Delete">
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  onClick={() => {
                                    modals.open({
                                      size: 'lg',
                                      title: <Title order={5}>Delete Custom Domain</Title>,
                                      children: (
                                        <>
                                          <Text mb={16} fz={14}>
                                            Are you sure you want to delete this custom domain? This
                                            action cannot be undone.
                                          </Text>
                                          <Alert
                                            mb={16}
                                            color="red"
                                            title="Warning"
                                            style={{
                                              color: 'var(--mantine-color-red-6)',
                                            }}
                                            icon={<FontAwesomeIcon icon={faExclamationCircle} />}
                                          >
                                            <Text fz={14} fw={500}>
                                              Your campaigns using this domain will be affected. Your links will be reverted to the default short domain and some of them may not work properly if the same slug (unique identifier) is already used by other users.
                                            </Text>
                                          </Alert>
                                          <Flex justify={'center'}>
                                            <Button
                                              variant="outline"
                                              color="red"
                                              mr={12}
                                              onClick={() =>
                                                handleDeleteCustomDomain(
                                                  item,
                                                  DELETE_WORKSPACE_OPTIONS.ONLY_THIS_WORKSPACE
                                                )
                                              }
                                            >
                                              Delete from this workspace
                                            </Button>
                                            {user?.migrated_to_v2 && <Button
                                              color="red"
                                              onClick={() =>
                                                handleDeleteCustomDomain(
                                                  item,
                                                  DELETE_WORKSPACE_OPTIONS.ALL_WORKSPACES
                                                )
                                              }
                                            >
                                              Delete from all workspaces
                                            </Button>}
                                          </Flex>
                                        </>
                                      ),
                                    });
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </ActionIcon>
                              </Tooltip>}
                            </Flex>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </>
                )}
              </Table.Tbody>
            </Table>
          </Box>

          {/* Domain Purchase Modal */}
          <DomainPurchaseModal
            opened={purchaseModalOpened}
            setOpened={setPurchaseModalOpened}
            plan={plan}
          />

          {/* DNS Records Modal */}
          <DNSRecordsModal
            opened={dnsModalOpened}
            setOpened={setDnsModalOpened}
            records={records}
          />
        </>
      )}

    </>
  );
}
